
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useUserRoles } from "@/hooks/useUserRoles";
import { supabase } from '@/integrations/supabase/client';
import { toast } from "@/hooks/use-toast";
import { Loader2, CreditCard, CheckCircle, ExternalLink } from "lucide-react";
import { Tables } from "@/integrations/supabase/types";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import PaymentLinkModal from './PaymentLinkModal';
import { usePaymentFlowConfig } from '@/hooks/usePaymentFlowConfig';

type Quote = Tables<'quotes'>;

interface QuotePaymentButtonsProps {
  quote: Quote;
  onPaymentUpdate?: () => void;
}

const QuotePaymentButtons: React.FC<QuotePaymentButtonsProps> = ({
  quote,
  onPaymentUpdate
}) => {
  const { user } = useAuth();
  const { roles } = useUserRoles();
  const [isConfirmingPayment, setIsConfirmingPayment] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const { shouldSkipPayment, isLoading } = usePaymentFlowConfig();

  const isAdmin = roles.isAdmin;
  const hasPaymentLink = !!quote.payment_url;
  const isPaymentConfirmed = quote.payment_confirmed;

  const handleConfirmPayment = async () => {
    if (!user?.id) return;

    setIsConfirmingPayment(true);
    try {
      const { data, error } = await supabase.rpc('confirm_quote_payment', {
        p_quote_id: quote.id,
        p_confirmed_by: user.id,
        p_payment_method: 'manual_confirmation'
      });

      if (error) throw error;

      const result = data as { success?: boolean; error?: string };

      if (result?.success) {
        toast({
          title: "Payment Confirmed",
          description: "Payment has been manually confirmed",
        });
        onPaymentUpdate?.();
      } else {
        throw new Error(result?.error || 'Failed to confirm payment');
      }
    } catch (error) {
      console.error('Error confirming payment:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to confirm payment",
        variant: "destructive",
      });
    } finally {
      setIsConfirmingPayment(false);
    }
  };

  const handlePayNow = () => {
    if (quote.payment_url) {
      window.open(quote.payment_url, '_blank', 'noopener,noreferrer');
    }
  };

  // Don't show buttons if payment is already confirmed
  if (isPaymentConfirmed) {
    return null;
  }

  // Hide payment buttons while loading or if payment should be skipped (Swift account flow)
  if (isLoading || shouldSkipPayment) {
    return null;
  }

  return (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        {/* Admin Buttons - Only for admin users */}
        {isAdmin && (
          <>
            {!hasPaymentLink && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsPaymentModalOpen(true)}
                className="h-8 px-3 text-xs"
              >
                <CreditCard className="w-3 h-3 mr-1" />
                Add Payment Link
              </Button>
            )}
            
            <Button
              variant="outline"
              size="sm"
              onClick={handleConfirmPayment}
              disabled={isConfirmingPayment}
              className="h-8 px-3 text-xs"
            >
              {isConfirmingPayment && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
              <CheckCircle className="w-3 h-3 mr-1" />
              Confirm Payment
            </Button>
          </>
        )}

        {/* Pay Now Button - Visible to ALL users viewing the quote */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handlePayNow}
                  disabled={!hasPaymentLink}
                  className={`h-8 px-3 text-xs ${
                    !hasPaymentLink 
                      ? 'opacity-50 cursor-not-allowed' 
                      : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  <ExternalLink className="w-3 h-3 mr-1" />
                  Pay Now
                </Button>
              </div>
            </TooltipTrigger>
            {!hasPaymentLink && (
              <TooltipContent>
                <p>Awaiting Payment Link. Check back shortly.</p>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Payment Link Modal */}
      <PaymentLinkModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        quote={quote}
        onPaymentUpdate={onPaymentUpdate}
      />
    </>
  );
};

export default QuotePaymentButtons;
